import React from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import { useSlotGroup } from '@vibesites/runtime/react';
import { EditableSlotGroup } from '@vibesites/runtime/react';

const scheduleTourLinkDef = {
  label: 'Schedule Tour Link',
  fields: {
    url: {
      label: 'URL',
      type: 'url',
      defaultField: 'property_apply_now_url',
      suggestedFields: ['property_apply_now_url', 'property_website_url'],
    },
    label: { label: 'Link Text', type: 'text', defaultValue: 'Schedule A Tour' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

export function Navigation() {
  const scheduleTourLink = useSlotGroup('navigation.schedule_tour_link', scheduleTourLinkDef);

  return (
    <nav className="bg-[var(--vibe-color-surface)] border-b border-[var(--vibe-color-border)] sticky top-20 z-40">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-12 overflow-x-auto">
          <div className="flex items-center gap-6">
            <a href="/" className="rise-nav-darken-hover font-body text-sm text-[var(--vibe-color-text-muted)] transition-colors whitespace-nowrap">
              Home
            </a>
            <a href="/virtual-tour" className="rise-nav-darken-hover font-body text-sm text-[var(--vibe-color-text-muted)] transition-colors whitespace-nowrap">
              Virtual Tour
            </a>
            <a href="/amenities" className="rise-nav-darken-hover font-body text-sm text-[var(--vibe-color-text-muted)] transition-colors whitespace-nowrap">
              Amenities
            </a>
            <a href="/gallery" className="rise-nav-darken-hover font-body text-sm text-[var(--vibe-color-text-muted)] transition-colors whitespace-nowrap">
              Gallery
            </a>
            <a href="/neighborhood" className="rise-nav-darken-hover font-body text-sm text-[var(--vibe-color-text-muted)] transition-colors whitespace-nowrap">
              Neighborhood
            </a>
            <a href="/contact-us" className="rise-nav-darken-hover font-body text-sm text-[var(--vibe-color-text-muted)] transition-colors whitespace-nowrap">
              Contact Us
            </a>
          </div>

          <EditableSlotGroup groupPath="navigation.schedule_tour_link" definition={scheduleTourLinkDef}>
            <a
              href={scheduleTourLink.url || '/schedule-a-tour'}
              target={scheduleTourLink._target || '_self'}
              className="hidden md:inline-flex items-center px-4 py-1.5 text-xs font-medium tracking-wider uppercase bg-[var(--vibe-color-primary)] text-primary-foreground hover:bg-[var(--vibe-color-secondary)] hover:text-secondary-foreground transition-colors whitespace-nowrap"
            >
              {scheduleTourLink.label || 'Schedule A Tour'}
            </a>
          </EditableSlotGroup>
        </div>
      </div>
      {/* Nav links: animated underline on hover (no color shift). The
          underline rides the text baseline via `text-decoration-*` so it
          tracks the text correctly regardless of the link's padding —
          works equally well in this padding-less sub-nav, the padded
          header `DesktopNavLink`, the More-dropdown rows, and the mobile
          drawer rows. Header.tsx repeats the same class in its own
          <style> block — duplicate rules are harmless. */}
      <style>{`
        .rise-nav-darken-hover {
          text-decoration-line: underline;
          text-decoration-color: transparent;
          text-decoration-thickness: 1.5px;
          text-underline-offset: 6px;
          transition: text-decoration-color 0.25s ease, text-underline-offset 0.25s ease, color 0.2s ease;
        }
        .rise-nav-darken-hover:hover {
          text-decoration-color: currentColor;
          text-underline-offset: 4px;
        }
      `}</style>
    </nav>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'Navigation',
  displayName: 'Navigation',
  slotGroups: {
    'navigation.schedule_tour_link': scheduleTourLinkDef,
  },
};

(Navigation as any).contentSchema = contentSchema;

export default Navigation;
